import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";

import { Badge, Button, useConfirm } from "@/ui";
import { useDeleteSpot, useSpots } from "@/features/queries";
import { noAutofill } from "@/lib/noAutofill";
import type { PlaceType } from "@/types/api";

const PLACE_LABEL: Record<PlaceType, string> = {
  outdoor_spot: "Улица",
  indoor_hall: "Зал",
  store: "Магазин",
};

export function AdminSpotsPage() {
  const navigate = useNavigate();
  const { data: spots = [], isLoading } = useSpots();
  const del = useDeleteSpot();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return spots;
    return spots.filter((s) => {
      const creator = (s.creator_username ?? "").toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q) ||
        creator.includes(q)
      );
    });
  }, [search, spots]);

  return (
    <div>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 className="h1">Площадки</h1>
        <Button leading={<Plus size={16} />} onClick={() => navigate("/admin/spots/new")}>
          Добавить
        </Button>
      </header>

      {spots.length > 0 && (
        <SearchBar value={search} onChange={setSearch} />
      )}

      {isLoading ? (
        <p className="body">Загрузка…</p>
      ) : spots.length === 0 ? (
        <div
          style={{
            padding: "64px 16px",
            textAlign: "center",
            background: "var(--bg-section)",
            borderRadius: "var(--r-lg)",
          }}
        >
          <h2 className="h2">Площадок пока нет</h2>
          <p className="body" style={{ color: "var(--fg2)", marginTop: 8 }}>
            Добавь первую — она появится на карте.
          </p>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate("/admin/spots/new")}>Добавить площадку</Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: "32px 16px",
            textAlign: "center",
            background: "var(--bg-section)",
            borderRadius: "var(--r-lg)",
            color: "var(--fg2)",
          }}
        >
          Ничего не нашли.
        </div>
      ) : (
        <div
          className="sp-admin-table-wrap"
          style={{ background: "var(--bg-section)", borderRadius: "var(--r-lg)" }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-body)" }}>
            <thead className="sp-admin-table-head">
              <tr style={{ background: "var(--bg-secondary)" }}>
                <Th>Название</Th>
                <Th>Тип</Th>
                <Th>Адрес</Th>
                <Th>Создатель</Th>
                <Th>Координаты</Th>
                <Th>Столы</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} style={{ borderTop: "1px solid var(--separator)" }}>
                  <Td><strong>{s.name}</strong></Td>
                  <Td><Badge kind="neutral">{PLACE_LABEL[s.place_type]}</Badge></Td>
                  <Td>{s.address}</Td>
                  <Td>{s.creator_username ? `@${s.creator_username}` : "—"}</Td>
                  <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    {s.latitude.toFixed(4)}, {s.longitude.toFixed(4)}
                  </Td>
                  <Td>{s.tables_count ?? "—"}</Td>
                  <Td>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        title="Редактировать"
                        onClick={() => navigate(`/admin/spots/${s.id}/edit`)}
                        style={iconBtn()}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        title="Удалить"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Удалить площадку?",
                            description: `«${s.name}» исчезнет из каталога.`,
                            confirmLabel: "Удалить",
                            tone: "danger",
                          });
                          if (ok) del.mutate(s.id);
                        }}
                        style={iconBtn("var(--fg-danger)")}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SearchBar({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "var(--bg-section)",
        borderRadius: "var(--r-md)",
        padding: "8px 12px",
        marginBottom: 12,
      }}
    >
      <Search size={16} color="var(--fg3)" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Поиск: название, адрес или @создатель"
        {...noAutofill}
        style={{
          flex: 1,
          border: 0,
          outline: 0,
          background: "transparent",
          fontFamily: "var(--font-body)",
          fontSize: 14,
          color: "var(--fg1)",
          minWidth: 0,
        }}
      />
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th style={{
      textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--fg3)",
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: "12px", fontSize: 14, verticalAlign: "middle", ...style }}>{children}</td>;
}

function iconBtn(color: string = "var(--fg1)"): React.CSSProperties {
  return {
    width: 32, height: 32, borderRadius: "var(--r-md)",
    border: 0, background: "var(--bg-secondary)", color,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer",
  };
}
